// src/components/study/PotentialPatientsCard.js
//
// "Find Potential Patients" on the study page.
//
// The admin has typed the study's inclusion/exclusion criteria (Edit Study).
// This card:
//   1. has the AI turn those criteria into rules over RegX's patient fields
//      (once; cached on the study, re-run when the criteria change),
//   2. loads the site's patients and evaluates the rules in the browser,
//   3. shows the shortlist, why each patient is on it, and what it could NOT
//      check — and exports the same as a PDF.
//
// Nothing about a patient leaves the browser. See eligibilityMatch.js and
// studyScreeningService.js for the two halves.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Sparkles,
  Loader2,
  FileDown,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  HelpCircle,
  RefreshCw,
  ChevronRight,
  ExternalLink,
} from "lucide-react";
import StudyCard from "./StudyCard";
import { auth } from "../../firebase";
import {
  deriveEligibilityQuery,
  saveEligibilityQuery,
  eligibilityQueryIsStale,
  loadPatientsForMatching,
  patientDetailPath,
} from "../../services/studyScreeningService";
import { rankCandidates, describeRule, DEFAULT_SKIPPED_STATUSES } from "../../services/eligibilityMatch";
import { exportPotentialPatientsPdf } from "../../services/potentialPatientsPdf";
import { diagnosisLabel, statusLabel } from "../../constants/diseaseOptions";

const btnPrimary =
  "group relative inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-xs font-semibold text-white hover:shadow-[0_10px_30px_-5px_rgba(139,92,246,0.6)] active:scale-[0.98] transition-all overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed";
const btnGhost =
  "inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-xs font-medium text-slate-200 hover:text-white transition disabled:opacity-50 disabled:cursor-not-allowed";

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
};

const PotentialPatientsCard = ({ study, canEdit, onStudyPatched }) => {
  const navigate = useNavigate();
  const hasCriteria = Boolean(String(study?.INCLUSION || "").trim() || String(study?.EXCLUSION || "").trim());
  const query = study?.eligibilityQuery || null;
  const rules = useMemo(() => (Array.isArray(query?.rules) ? query.rules : []), [query]);
  const unmapped = useMemo(() => (Array.isArray(query?.unmapped) ? query.unmapped : []), [query]);
  const stale = eligibilityQueryIsStale(study);

  const [deriving, setDeriving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState(null); // null = not loaded yet
  const [sheetError, setSheetError] = useState("");
  const [error, setError] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showRules, setShowRules] = useState(true);

  const loadPatients = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { patients: list, sheetError: se } = await loadPatientsForMatching();
      setPatients(list);
      setSheetError(se);
    } catch (e) {
      setError(e.message || "Could not load patients.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Rules already on the study -> show the shortlist without a click.
  useEffect(() => {
    if (rules.length && patients === null && !loading) loadPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules.length]);

  const derive = async () => {
    if (!canEdit) return;
    setDeriving(true);
    setError("");
    try {
      const result = await deriveEligibilityQuery({
        inclusion: study.INCLUSION || "",
        exclusion: study.EXCLUSION || "",
        disease: study.DISEASE || "",
        studyName: study.NAME || study.STUDY_ID || "",
      });
      const saved = await saveEligibilityQuery(study.id, result, {
        inclusion: study.INCLUSION,
        exclusion: study.EXCLUSION,
      });
      onStudyPatched && onStudyPatched({ eligibilityQuery: saved });
      if (patients === null) await loadPatients();
    } catch (e) {
      setError(e.message || "The AI could not derive a query from these criteria.");
    } finally {
      setDeriving(false);
    }
  };

  const ranked = useMemo(() => {
    if (!patients || !rules.length) return null;
    return rankCandidates(patients, rules, { skipStatuses: includeInactive ? [] : DEFAULT_SKIPPED_STATUSES });
  }, [patients, rules, includeInactive]);

  const ruleById = useMemo(() => new Map(rules.map((r) => [r.id, r])), [rules]);

  const exportPdf = () => {
    if (!ranked) return;
    exportPotentialPatientsPdf({
      study,
      rules,
      unmapped,
      candidates: ranked.candidates,
      stats: ranked,
      generatedBy: auth.currentUser?.email || "",
    });
  };

  const inc = rules.filter((r) => r.side === "inclusion");
  const exc = rules.filter((r) => r.side === "exclusion");

  return (
    <StudyCard
      title="Potential Patients"
      subtitle={
        rules.length
          ? `${rules.length} criteria checked against RegX · derived ${fmtWhen(query?.derivedAt) || "earlier"}`
          : "Query RegX for patients who fit the inclusion / exclusion criteria"
      }
      icon={<Users className="w-3.5 h-3.5 text-purple-300" />}
      actions={
        <>
          {ranked && (
            <button type="button" onClick={exportPdf} className={btnGhost} title="Download the shortlist as a PDF">
              <FileDown className="w-3.5 h-3.5" />
              Export as PDF
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={derive}
              disabled={!hasCriteria || deriving}
              className={rules.length ? btnGhost : btnPrimary}
              title={
                hasCriteria
                  ? "Have the AI read the criteria and build the query"
                  : "Add inclusion / exclusion criteria with Edit Study first"
              }
            >
              {deriving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : rules.length ? (
                <RefreshCw className="w-3.5 h-3.5" />
              ) : (
                <Sparkles className="w-3.5 h-3.5 relative" />
              )}
              <span className="relative">
                {deriving ? "Reading criteria…" : rules.length ? "Re-run AI" : "Find Potential Patients"}
              </span>
            </button>
          )}
        </>
      }
    >
      {!hasCriteria && (
        <p className="text-xs text-slate-400">
          This study has no inclusion or exclusion criteria yet. Add them with <span className="text-slate-200">Edit Study</span>,
          then come back here.
        </p>
      )}

      {hasCriteria && !rules.length && !deriving && !error && (
        <p className="text-xs text-slate-400">
          {canEdit
            ? "Click Find Potential Patients. The AI reads the criteria and turns them into checks on age, diagnosis, spirometry, eosinophils, exacerbations and the other fields RegX records. No patient data is sent to the AI."
            : "An admin hasn't run the patient query for this study yet."}
        </p>
      )}

      {error && (
        <div className="mb-3 flex items-start gap-2 rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {stale && rules.length > 0 && (
        <div className="mb-3 flex items-start gap-2 rounded-xl border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            The inclusion / exclusion text has changed since these checks were derived.
            {canEdit ? " Re-run the AI to bring them up to date." : " Ask an admin to re-run the AI."}
          </span>
        </div>
      )}

      {rules.length > 0 && (
        <details
          open={showRules}
          onToggle={(e) => setShowRules(e.currentTarget.open)}
          className="group rounded-xl border border-white/10 bg-white/[0.03] p-3 mb-3"
        >
          <summary className="cursor-pointer flex items-center gap-2 text-xs font-semibold text-white list-none">
            <Sparkles className="w-3.5 h-3.5 text-purple-300" />
            What RegX is checking
            <span className="text-[10px] font-normal text-slate-400">
              {inc.length} include · {exc.length} exclude · {unmapped.length} manual
            </span>
            <ChevronRight className="ml-auto w-3.5 h-3.5 text-slate-400 group-open:rotate-90 transition-transform" />
          </summary>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <RuleList title="Include when" tone="emerald" rules={inc} />
            <RuleList title="Exclude when" tone="rose" rules={exc} />
          </div>
          {unmapped.length > 0 && (
            <div className="mt-3 rounded-lg border border-amber-400/20 bg-amber-500/[0.06] p-2.5">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-200 mb-1.5 flex items-center gap-1.5">
                <HelpCircle className="w-3 h-3" />
                Not on record in RegX — check by hand
              </div>
              <ul className="space-y-1">
                {unmapped.map((u, i) => (
                  <li key={i} className="text-[11px] text-slate-300 leading-snug">
                    <span className={`font-mono text-[9px] mr-1.5 ${u.side === "exclusion" ? "text-rose-300" : "text-emerald-300"}`}>
                      {u.side === "exclusion" ? "EXCL" : "INCL"}
                    </span>
                    {u.criterion}
                    {u.reason && <span className="text-slate-500"> — {u.reason}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </details>
      )}

      {rules.length > 0 && loading && (
        <div className="flex items-center gap-2 text-xs text-slate-400 py-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Loading patients…
        </div>
      )}

      {sheetError && (
        <div className="mb-3 text-[11px] text-amber-200/90">
          Legacy sheet patients could not be loaded ({sheetError}); the list below is Firestore patients only.
        </div>
      )}

      {ranked && (
        <>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-slate-300 mb-2">
            <span>
              <span className="text-white font-semibold">{ranked.candidates.length}</span> candidate
              {ranked.candidates.length === 1 ? "" : "s"}
            </span>
            <span className="text-slate-500">·</span>
            <span>{ranked.ruledOut} ruled out by a criterion</span>
            <span className="text-slate-500">·</span>
            <span>{ranked.skipped} skipped</span>
            <span className="text-slate-500">·</span>
            <span>{ranked.total} records checked</span>
            <label className="ml-auto inline-flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                className="w-3.5 h-3.5 accent-purple-500"
              />
              <span className="text-slate-400">Include enrolled / deceased</span>
            </label>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
            <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_3rem_minmax(0,1.4fr)_minmax(0,1.6fr)_4rem_5rem_1.5rem] gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-400 border-b border-white/5">
              <span>Patient</span>
              <span>MRN</span>
              <span>Age</span>
              <span>Diagnosis</span>
              <span>Status</span>
              <span className="text-emerald-300">Met</span>
              <span className="text-slate-400">Unchecked</span>
              <span />
            </div>
            <div className="max-h-[480px] overflow-y-auto divide-y divide-white/5 scrollbar-thin">
              {ranked.candidates.length === 0 && (
                <div className="px-3 py-4 text-xs text-slate-400">
                  No patient on record fits these criteria without contradiction.
                </div>
              )}
              {ranked.candidates.map((p) => (
                <button
                  key={`${p.source}:${p.id}`}
                  type="button"
                  // Tell the patient page where this came from, so its Back
                  // (and its save) return to the study rather than the
                  // coordinator list. Same convention PatientQueryPage uses.
                  onClick={() =>
                    navigate(patientDetailPath(p), {
                      state: { from: "study", studyId: study.id },
                    })
                  }
                  className="w-full text-left grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_3rem_minmax(0,1.4fr)_minmax(0,1.6fr)_4rem_5rem_1.5rem] gap-2 px-3 py-2 items-center hover:bg-white/[0.04] transition"
                  title={
                    p.match.unknown.length
                      ? `Unchecked: ${p.match.unknown.map((id) => describeRule(ruleById.get(id))).join("; ")}`
                      : "Every criterion RegX can check is met"
                  }
                >
                  <span className="text-xs text-slate-100 truncate">{p.name || "(no name)"}</span>
                  <span className="text-[11px] font-mono text-slate-300 truncate">{p.mrn || "—"}</span>
                  <span className="text-[11px] text-slate-300">{p.age ?? "—"}</span>
                  <span className="text-[11px] text-slate-300 truncate">{(p.diagnosis || []).map(diagnosisLabel).join(", ") || "—"}</span>
                  <span className="text-[11px] text-slate-400 truncate">{statusLabel(p.status) || "—"}</span>
                  <span className="text-[11px] font-semibold text-emerald-300">
                    {p.match.met.length}/{rules.length}
                  </span>
                  <span className="text-[11px] text-slate-400">{p.match.unknown.length}</span>
                  <ExternalLink className="w-3 h-3 text-slate-500" />
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </StudyCard>
  );
};

const RuleList = ({ title, tone, rules }) => {
  const Icon = tone === "rose" ? XCircle : CheckCircle2;
  const color = tone === "rose" ? "text-rose-300" : "text-emerald-300";
  return (
    <div>
      <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1.5 flex items-center gap-1.5 ${color}`}>
        <Icon className="w-3 h-3" />
        {title}
      </div>
      {rules.length === 0 ? (
        <p className="text-[11px] text-slate-500">None</p>
      ) : (
        <ul className="space-y-1">
          {rules.map((r) => (
            <li key={r.id} className="text-[11px] text-slate-200 leading-snug">
              <span className="text-white">{describeRule(r)}</span>
              {r.criterion && <span className="block text-[10px] text-slate-500 truncate" title={r.criterion}>“{r.criterion}”</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PotentialPatientsCard;
